import { useFormContext } from 'react-hook-form';
import { lpCompany as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHistory } from 'react-router-dom';
import {
  createLpCompanyEditRoute,
  createLpCompanyRevaluationsRoute,
  LP_COMPANY_LIST,
} from 'adminConstants/routes';
import { useNotification } from 'hooks/ui/useNotification';
import ActionBar from './ActionBar';
import Page from 'components/new/Page';
import { Vehicle } from 'further-types/lp/vehicle';
import CompanyForm from './CompanyForm';
import CompanyOverview from './CompanyOverview';
import AvatarWithName from 'components/new/AvatarWithName';
import { Apartment } from '@mui/icons-material';
import TabsPanel from 'further-ui/components/TabsPanel';

type Props = {
  selectedVehicle: Vehicle;
  company?: { id: string; name: string; logoUrl?: string };
  companyId?: string;
  companyName?: string;
};

const CompanyPage: React.FC<Props> = ({ selectedVehicle, company }) => {
  const { handleSubmit } = useFormContext<rules.CompanySchemaFormValues>();
  const history = useHistory();
  const notification = useNotification();
  const mutationOpts = useHandleTrpcMutation();

  const { mutateAsync: createCompany } = api.lpCompany.create.useMutation(
    mutationOpts({ successMessage: 'Company created successfully.' }),
  );
  const { mutate: updateCompany } = api.lpCompany.update.useMutation(
    mutationOpts({ successMessage: 'Company updated successfully.' }),
  );

  const onSubmit = async (data: rules.CompanySchemaFormValues) => {
    try {
      if (company) {
        updateCompany({ companyId: company.id, ...data });
      } else {
        const newCompany = await createCompany(data);
        if (newCompany) {
          history.push(createLpCompanyEditRoute(newCompany._id));
        }
      }
    } catch (error) {
      console.error('Failed to save company:', error);
    }
  };

  const onError = () => {
    notification.error('Please fill in all required fields.');
  };

  const {
    data: assetPerformanceAggregations,
    isLoading: isAssetPerformanceLoading,
  } = api.asset.getAssetPerformance.useQuery(
    { assetId: company?.id ?? '', vehicleId: selectedVehicle._id },
    { enabled: !!company && !!selectedVehicle._id },
  );

  const hasInvestments = !!assetPerformanceAggregations?.firstInvestmentDate;

  const handleRevalueClick = company
    ? () => history.push(createLpCompanyRevaluationsRoute(company.id))
    : undefined;

  return (
    <Page
      tabTitle={company ? 'Holdings: View Holdings' : 'Holdings: Add Holding'}
      spacing="lg"
      goBackAction={LP_COMPANY_LIST}
      heading={
        company ? (
          <AvatarWithName
            icon={Apartment}
            priority="icon"
            avatarSize="lg"
            name={company.name}
            src={company.logoUrl}
          />
        ) : (
          'Create holding'
        )
      }
      actions={
        handleRevalueClick
          ? [
              {
                label: 'Revalue company',
                onClick: handleRevalueClick,
                disabled: !hasInvestments,
                tooltip: !hasInvestments
                  ? 'There are currently no units held. Please make an investment before revaluing units.'
                  : undefined,
              },
            ]
          : undefined
      }
      withHeadingDivider
      actionBar={
        <ActionBar
          onBack={() => history.push(LP_COMPANY_LIST)}
          onSave={handleSubmit(onSubmit, onError)}
        />
      }
    >
      {company ? (
        <TabsPanel
          useTabsUrls
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              urlParam: 'overview',
              content: (
                <CompanyOverview
                  selectedVehicle={selectedVehicle}
                  companyId={company.id}
                  assetPerformanceAggregations={assetPerformanceAggregations}
                  isAssetPerformanceLoading={isAssetPerformanceLoading}
                />
              ),
            },
            {
              key: 'about',
              label: 'About',
              urlParam: 'about',
              content: <CompanyForm selectedVehicle={selectedVehicle} />,
            },
          ]}
        />
      ) : (
        <CompanyForm selectedVehicle={selectedVehicle} />
      )}
    </Page>
  );
};

export default CompanyPage;
